'use client';

import classNames from 'classnames';
import dynamic from 'next/dynamic';
import { ComponentProps, useCallback, useMemo, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import TrailDetailsShareOption from '@alltrails/amplitude/enums/TrailDetailsShareOption';
import logTrailDetailsActionTapped from '@alltrails/amplitude/events/logTrailDetailsActionTapped';
import logTrailDetailsShareThisTrailOptionClicked from '@alltrails/amplitude/events/logTrailDetailsShareThisTrailOptionClicked';
import useUser from '@alltrails/context/hooks/useUser';
import { isSubscriber } from '@alltrails/context/utils/isSubscriber';
import { useFormatMessage } from '@alltrails/core/hooks/useFormatMessage';
import { useLanguageRegionCode } from '@alltrails/core/hooks/useLanguageRegionCode';
import { wrapUrlSafe } from '@alltrails/core/utils/wrapUrlSafe';
import IconButton from '@alltrails/denali/components/IconButton';
import useIsMobileSizedScreen from '@alltrails/denali/hooks/useIsMobileSizedScreen';
import ArrowSq from '@alltrails/denali/icons/ArrowSq';
import CheckCircle from '@alltrails/denali/icons/CheckCircle';
import CheckCircleFilled from '@alltrails/denali/icons/CheckCircleFilled';
import Download from '@alltrails/denali/icons/Download';
import Share from '@alltrails/denali/icons/Share';
import VerifiedFilled from '@alltrails/denali/icons/VerifiedFilled';
import CreateEventIconButton from '@alltrails/events/components/CreateEventIconButton';
import useNavigateBackInternal from '@alltrails/layout/hooks/useNavigateBackInternal';
import useReduxListItems from '@alltrails/lists/hooks/useReduxListItems';
import useTrailCompleted from '@alltrails/lists/hooks/useTrailCompleted';
import { getTrailShareObject, TrailPage } from '@alltrails/trails';
import type TrailDetails from '@alltrails/types/TrailDetails';
import SaveCta from '@/components/SaveCta';
import getTrailPageType from '@/utils/getTrailPageType';
import styles from './MobileCtaBar.module.scss';

const HitTheTrailModal = dynamic(() => import('@/components/HitTheTrailModal/HitTheTrailModal'), { ssr: false });
const ShareModal = dynamic(() => import('@alltrails/sharing/components/ShareModal/ShareModal'), { ssr: false });
const SignUpModal = dynamic(() => import('@alltrails/user-auth/components/SignUpModal'), { ssr: false });
const VerifiedCompletedDialog = dynamic(() => import('../VerifiedCompletedDialog/VerifiedCompletedDialog'), { ssr: false });

const messages = defineMessages({
  ADD_TO_COMPLETED: { defaultMessage: 'Add to completed' },
  BACK: { defaultMessage: 'Back' },
  BACK_TO_EXPLORE: { defaultMessage: ' Back to Explore' },
  DOWNLOAD: { defaultMessage: 'Download' },
  REMOVE_FROM_COMPLETED: { defaultMessage: 'Remove from completed' },
  SHARE: { defaultMessage: 'Share' },
  VERIFIED: { defaultMessage: 'Verified completed' }
});

type MobileCtaBarProps = {
  className?: string;
  name: string;
  slug: string;
  trailDetails: TrailDetails;
  trailId: number;
  trailPage: TrailPage;
  variant?: ComponentProps<typeof IconButton>['variant'];
};

const MobileCtaBar = ({ className, name, slug, trailDetails, trailId, trailPage, variant = 'transparent' }: MobileCtaBarProps) => {
  const intl = useIntl();
  const {
    formattedDefaultMessages: { ADD_TO_COMPLETED, BACK, BACK_TO_EXPLORE, DOWNLOAD, REMOVE_FROM_COMPLETED, SHARE, VERIFIED }
  } = useFormatMessage(messages);

  const isMobile = useIsMobileSizedScreen();
  const user = useUser();
  const languageRegionCode = useLanguageRegionCode();
  const { canNavigateBack, navigateBack } = useNavigateBackInternal();
  const [isDownloadModalOpen, setIsDownloadModalOpen] = useState(false);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [isSignupModalOpen, setIsSignupModalOpen] = useState(false);
  const [isVerifiedDialogOpen, setIsVerifiedDialogOpen] = useState(false);

  const isBaseUser = user && !isSubscriber(user);

  const { isVerified } = useReduxListItems();
  const { completed, toggleTrailCompleted } = useTrailCompleted(trailId);
  const verified = isVerified('trail', trailId);

  const trailPageType = getTrailPageType(trailDetails);

  const onAddToCompleteClick = useCallback(() => {
    logTrailDetailsActionTapped({
      trail_id: trailId.toString(),
      trail_page_action: completed ? 'remove from completed' : 'add to completed',
      trail_page_location: 'hero image inset',
      trail_page_type: trailPageType
    });
    if (user) {
      toggleTrailCompleted();
    } else {
      setIsSignupModalOpen(true);
    }
  }, [completed, trailId, user, toggleTrailCompleted, trailPageType]);

  const onVerifyClick = () => {
    logTrailDetailsActionTapped({
      trail_id: trailId.toString(),
      trail_page_action: 'add to completed',
      trail_page_location: 'hero image inset',
      trail_page_type: trailPageType
    });
    setIsVerifiedDialogOpen(true);
  };

  const onShareOptionClick = useCallback(
    (shareOption: TrailDetailsShareOption) => {
      logTrailDetailsShareThisTrailOptionClicked({ share_option_clicked: shareOption, trail_id: trailId.toString() });
    },
    [trailId]
  );

  const shareObject = useMemo(() => getTrailShareObject({ id: trailId, slug, name } as TrailDetails), [name, slug, trailId]);

  const onShareClick = useCallback(() => {
    logTrailDetailsActionTapped({
      trail_id: trailId.toString(),
      trail_page_action: 'share',
      trail_page_location: 'hero image inset',
      trail_page_type: trailPageType
    });

    if (isMobile) {
      const title = intl.formatMessage({ defaultMessage: '{name} on AllTrails' }, { name: shareObject.name });
      const params = new URLSearchParams();

      if (user?.referralCode) {
        params.set('sh', user.referralCode);
      } else {
        // Identifies share links from unauthenticated users for virality attribution
        params.set('sh', 'true');
      }

      const search = params.toString();
      const url = window.location.origin + wrapUrlSafe(`/${shareObject.slug}${search.length > 0 ? `?${search}` : ''}`, languageRegionCode);

      try {
        navigator.share({
          title,
          text: title,
          url
        });
      } catch {
        setIsShareModalOpen(true);
      }
    } else {
      setIsShareModalOpen(true);
    }
  }, [intl, isMobile, languageRegionCode, shareObject.name, shareObject.slug, trailId, trailPageType, user]);

  return (
    <div className={classNames(styles.ctaBar, className)}>
      <IconButton
        className={classNames(styles.backToExplore)}
        icon={{ Component: ArrowSq, orientation: 'left' }}
        onClick={navigateBack}
        size="sm"
        title={canNavigateBack && isMobile ? BACK : BACK_TO_EXPLORE}
        variant={variant}
      />
      {!user ? (
        <IconButton
          icon={{ Component: Download }}
          onClick={() => {
            logTrailDetailsActionTapped({
              trail_id: trailId.toString(),
              trail_page_action: 'download overflow menu button',
              trail_page_location: 'hero image inset',
              trail_page_type: trailPageType
            });
            setIsDownloadModalOpen(true);
          }}
          size="sm"
          title={DOWNLOAD}
          variant={variant}
        />
      ) : (
        <IconButton icon={{ Component: Share }} onClick={onShareClick} size="sm" title={SHARE} variant={variant} />
      )}
      <CreateEventIconButton size="sm" mapId={trailDetails.id} mapType="T" variant={variant} />
      {verified ? (
        <IconButton icon={{ Component: VerifiedFilled }} onClick={onVerifyClick} size="sm" title={VERIFIED} variant={variant} />
      ) : (
        <IconButton
          icon={{ Component: completed ? CheckCircleFilled : CheckCircle }}
          onClick={onAddToCompleteClick}
          size="sm"
          title={completed ? REMOVE_FROM_COMPLETED : ADD_TO_COMPLETED}
          variant={variant}
        />
      )}
      {isBaseUser ? (
        <IconButton
          icon={{ Component: Download }}
          onClick={() => {
            logTrailDetailsActionTapped({
              trail_id: trailId.toString(),
              trail_page_action: 'download overflow menu button',
              trail_page_location: 'hero image inset',
              trail_page_type: trailPageType
            });
            setIsDownloadModalOpen(true);
          }}
          size="sm"
          title={DOWNLOAD}
          variant={variant}
        />
      ) : (
        <SaveCta buttonLocation="hero image inset" trailId={trailId} variant={variant} size="sm" trailPageType={trailPageType} />
      )}
      {isDownloadModalOpen && (
        <HitTheTrailModal
          isOpen={isDownloadModalOpen}
          onCloseRequest={() => setIsDownloadModalOpen(false)}
          title={DOWNLOAD}
          trailDetails={trailDetails}
          trailPage={trailPage}
        />
      )}
      {isShareModalOpen && (
        <ShareModal isOpen onClose={() => setIsShareModalOpen(false)} onShareOptionClick={onShareOptionClick} shareObject={shareObject} />
      )}
      {isVerifiedDialogOpen && <VerifiedCompletedDialog onClose={() => setIsVerifiedDialogOpen(false)} />}
      {isSignupModalOpen && (
        <SignUpModal
          isOpen={isSignupModalOpen}
          closeModal={() => setIsSignupModalOpen(false)}
          deeplinkData={{ pageId: trailId, pageType: 'trail' }}
          onSuccess={() => {
            setIsSignupModalOpen(false);
            toggleTrailCompleted();
          }}
          trigger="Add Trail to Completed List"
          upsellTrigger="Add Trail to Completed List"
        />
      )}
    </div>
  );
};

export default MobileCtaBar;
